import { Injectable, inject, signal } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { MemberCompanyProfileDto, MemberLoginService } from './login.service';
import { Observable, tap, throwError } from 'rxjs';

/**
 * Central Auth State Manager
 * Keeps login token + selected company profile in sync with localStorage
 */
@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly memberLogin = inject(MemberLoginService);
  private readonly document = inject(DOCUMENT);

  private readonly AUTH_TOKEN_KEY = 'auth_token';
  private readonly AUTH_EMAIL_KEY = 'AuthenticatedEmail';
  private readonly AUTH_CNIC_KEY = 'AuthenticatedCNIC';
  private readonly PROFILE_KEY = 'member_profile';
  private readonly SELECTED_MEMBER_ID = 'SelectedMemberID';

  /** 🔹 reactive signals */
  readonly isLoggedIn = signal<boolean>(false);
  readonly profile = signal<MemberCompanyProfileDto | null>(null);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Restore persisted state
    const token = localStorage.getItem(this.AUTH_TOKEN_KEY);
    const storedProfile = localStorage.getItem(this.PROFILE_KEY);

    this.isLoggedIn.set(!!token);

    if (storedProfile) {
      try {
        this.profile.set(JSON.parse(storedProfile));
      } catch {
        localStorage.removeItem(this.PROFILE_KEY);
      }
    }

    // Listen for multi-tab sign-out
    window.addEventListener('storage', e => {
      if (e.key === this.AUTH_TOKEN_KEY && e.newValue === null) {
        this.clearAuth(false);
      }
    });
  }

  // ====== AUTH KEYS ======

  /** Called after successful login */
  setAuth(email: string, token: string, cnicNo: string) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.AUTH_EMAIL_KEY, email);
      localStorage.setItem(this.AUTH_TOKEN_KEY, token);
      localStorage.setItem(this.AUTH_CNIC_KEY, cnicNo);
    }
    this.isLoggedIn.set(true);
  }

  getAuthEmail(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return localStorage.getItem(this.AUTH_EMAIL_KEY);
  }

  getAuthCnic(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return localStorage.getItem(this.AUTH_CNIC_KEY);
  }

  // ====== SELECTED MEMBERSHIP ======

  setSelectedMemberId(memberId: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.SELECTED_MEMBER_ID, memberId);
  }

  getSelectedMemberId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return localStorage.getItem(this.SELECTED_MEMBER_ID);
  }

  clearSelectedMemberId() {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.removeItem(this.SELECTED_MEMBER_ID);
  }

  // ====== COMPANY PROFILE ======

  /** Save or clear selected company profile */
  setProfile(p: MemberCompanyProfileDto | null) {
    if (isPlatformBrowser(this.platformId)) {
      if (p) localStorage.setItem(this.PROFILE_KEY, JSON.stringify(p));
      else localStorage.removeItem(this.PROFILE_KEY);
    }
    this.profile.set(p);
  }

  /**
   * Refresh company profile using SelectedMemberID
   * (replaces old /Member/by-email/{email})
   */
  refreshCompanyProfileBySelectedMemberId(): Observable<MemberCompanyProfileDto> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('Not in browser'));
    }

    const memberId = localStorage.getItem(this.SELECTED_MEMBER_ID);
    if (!memberId) return throwError(() => new Error('No selected membership found'));

    return this.memberLogin.fetchCompanyProfileByMemberId(memberId).pipe(
      tap(p => this.setProfile(p))
    );
  }

  // ====== LOGOUT ======

  clearAuth(showReload = true) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.AUTH_TOKEN_KEY);
      localStorage.removeItem(this.AUTH_EMAIL_KEY);
      localStorage.removeItem(this.AUTH_CNIC_KEY);
      localStorage.removeItem(this.SELECTED_MEMBER_ID);
      localStorage.removeItem(this.PROFILE_KEY);
    }

    this.profile.set(null);
    this.isLoggedIn.set(false);

    if (showReload && isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.document.location.reload(), 200);
    }
  }

  /** Direct signal mutation if needed */
  setLoggedIn(v: boolean) {
    this.isLoggedIn.set(v);
  }
}
